<template>
  <div v-if="images && images.length > 0" class="artist-gallery">
    <h2 class="text-6xl mb-12 font-serif" :class="textColorClass">
      {{ t('pages.artist.gallery.title') }}
    </h2>
    
    <!-- 1 Image: Full width -->
    <div v-if="images.length === 1" class="gallery-1">
      <div class="relative w-full aspect-[4/3] border overflow-hidden" :class="borderColorClass">
        <SmartImage
          :src="images[0]"
          :slug="artistSlug"
          :alt="getAlt(0)"
          image-class="w-full h-full object-cover"
          sizes="sm:100vw md:800px lg:1200px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </div>
    </div>

    <!-- 2 Images: Side by side -->
    <div v-else-if="images.length === 2" class="gallery-2 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div
        v-for="(image, index) in images"
        :key="index"
        class="relative aspect-square border overflow-hidden"
        :class="borderColorClass"
      >
        <SmartImage
          :src="image"
          :slug="artistSlug"
          :alt="getAlt(index)"
          image-class="w-full h-full object-cover"
          sizes="sm:200px md:400px lg:500px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </div>
    </div>

    <!-- 3 Images: 2+1 layout -->
    <div v-else-if="images.length === 3" class="gallery-3 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="relative aspect-square border overflow-hidden md:col-span-1" :class="borderColorClass">
        <SmartImage
          :src="images[0]"
          :slug="artistSlug"
          :alt="getAlt(0)"
          image-class="w-full h-full object-cover"
          sizes="sm:200px md:400px lg:500px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </div>
      <div class="relative aspect-square border overflow-hidden md:col-span-1" :class="borderColorClass">
        <SmartImage
          :src="images[1]"
          :slug="artistSlug"
          :alt="getAlt(1)"
          image-class="w-full h-full object-cover"
          sizes="sm:200px md:400px lg:500px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </div>
      <div class="relative aspect-square border overflow-hidden md:col-span-2" :class="borderColorClass">
        <SmartImage
          :src="images[2]"
          :slug="artistSlug"
          :alt="getAlt(2)"
          image-class="w-full h-full object-cover"
          sizes="sm:100vw md:800px lg:1000px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </div>
    </div>

    <!-- 4 Images: 2x2 grid -->
    <div v-else-if="images.length === 4" class="gallery-4 grid grid-cols-2 gap-4">
      <div
        v-for="(image, index) in images"
        :key="index"
        class="relative aspect-square border overflow-hidden"
        :class="borderColorClass"
      >
        <SmartImage
          :src="image"
          :slug="artistSlug"
          :alt="getAlt(index)"
          image-class="w-full h-full object-cover"
          sizes="sm:200px md:400px lg:500px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </div>
    </div>

    <!-- 5 Images: 2+3 layout (large left, 4 small right) -->
    <div v-else-if="images.length === 5" class="gallery-5 grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="relative aspect-square border overflow-hidden md:col-span-1 md:row-span-2" :class="borderColorClass">
        <SmartImage
          :src="images[0]"
          :slug="artistSlug"
          :alt="getAlt(0)"
          image-class="w-full h-full object-cover"
          sizes="sm:200px md:400px lg:500px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </div>
      <div class="relative aspect-square border overflow-hidden md:col-span-1" :class="borderColorClass">
        <SmartImage
          :src="images[1]"
          :slug="artistSlug"
          :alt="getAlt(1)"
          image-class="w-full h-full object-cover"
          sizes="sm:200px md:400px lg:500px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </div>
      <div class="relative aspect-square border overflow-hidden md:col-span-1" :class="borderColorClass">
        <SmartImage
          :src="images[2]"
          :slug="artistSlug"
          :alt="getAlt(2)"
          image-class="w-full h-full object-cover"
          sizes="sm:200px md:400px lg:500px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </div>
      <div class="relative aspect-square border overflow-hidden md:col-span-1" :class="borderColorClass">
        <SmartImage
          :src="images[3]"
          :slug="artistSlug"
          :alt="getAlt(3)"
          image-class="w-full h-full object-cover"
          sizes="sm:200px md:400px lg:500px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </div>
      <div class="relative aspect-square border overflow-hidden md:col-span-1" :class="borderColorClass">
        <SmartImage
          :src="images[4]"
          :slug="artistSlug"
          :alt="getAlt(4)"
          image-class="w-full h-full object-cover"
          sizes="sm:200px md:400px lg:500px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </div>
    </div>

    <!-- Fallback: Grid for 6+ images -->
    <div v-else class="gallery-fallback grid grid-cols-2 md:grid-cols-3 gap-4">
      <div
        v-for="(image, index) in images"
        :key="index"
        class="relative aspect-square border overflow-hidden"
        :class="borderColorClass"
      >
        <SmartImage
          :src="image"
          :slug="artistSlug"
          :alt="getAlt(index)"
          image-class="w-full h-full object-cover"
          sizes="sm:200px md:280px lg:320px"
          :modifiers="{ quality: 'auto', format: 'auto' }"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '#imports'
import SmartImage from '~/components/SmartImage.vue'

interface Props {
  images: string[]
  textColorClass: string
  borderColorClass: string
  artistSlug?: string
}

const props = defineProps<Props>()
const { t } = useI18n()

const getAlt = (index: number) => {
  return t('pages.artist.gallery.alt', { index: index + 1 })
}
</script>

